'use client'

import type { WhoAmI } from '@tebuto/api/therapists/models/WhoAmI'
import { useTherapistsApiRequest } from '@tebuto/functions/hooks'
import { transformTebutoLink } from '@tebuto/functions/links'
import { LoadingScreen } from '@tebuto/layout/LoadingScreen'
import PageError from '@tebuto/layout/PageError'
import { ComponentSpinner } from '@tebuto/layout/Spinner'
import { usePathname } from 'next/navigation'
import { createContext, type JSX } from 'react'
import { resolveCurrentUserGuardView } from './current-user-guard-state'
import useRouter from './leave-page/useRouter'

export const CurrentUserContext = createContext<WhoAmI | null>(null)

let whoAmIBootstrapComplete = false

export function CurrentUserGuard({ children }: { children: React.ReactNode }): JSX.Element {
    const router = useRouter()
    const pathname = usePathname()

    const { data: userContext, error } = useTherapistsApiRequest('who-am-i', (api) => api.whoAmI.whoAmI(), {
        onError: () => {}
    })

    if (userContext) {
        whoAmIBootstrapComplete = true
    }

    const view = resolveCurrentUserGuardView({
        userContext,
        error,
        whoAmIBootstrapComplete
    })

    if (view === 'redirect-401') {
        const authorizeThenReturnUrl = new URL(`${process.env.NEXT_PUBLIC_THERAPISTS_AUTHORIZE_URL}`)
        authorizeThenReturnUrl.searchParams.set('returnTo', transformTebutoLink(pathname))
        router.push(authorizeThenReturnUrl.toString())
        return <>Redirecting...</>
    }

    if (view === 'page-error') {
        return <PageError reset={() => router.refresh()} />
    }

    if (view === 'loading-screen') {
        return <LoadingScreen />
    }

    if (view === 'block-spinner') {
        return <ComponentSpinner layout="block" />
    }

    if (!userContext) {
        return <ComponentSpinner layout="block" />
    }

    return <CurrentUserContext.Provider value={userContext}>{children}</CurrentUserContext.Provider>
}
